<script setup lang="ts">
import { useToggle } from '@vueuse/core'
import { Button, Divider } from 'primevue'
import { type Ref, ref } from 'vue'
import Calc from '@/components/Calc.vue'
import PaymentQR from '@/components/PaymentQR.vue'
import { PHONE, ROUTES } from '@/constants'
import { type UseToggle } from '@/types'
import { cleanPhone, isDesktop } from '@/utils'

interface Props {
    short?: boolean
}

const props = defineProps<Props>()

defineEmits<{ (e: 'onButtonClick', content: string): Awaited<Promise<void>> }>()

const [showMenu, toggleMenu]: UseToggle = useToggle()

const showQr: Ref<boolean> = ref(false)
</script>

<template>
    <PaymentQR v-model="showQr" />
    <div
        class="relative flex-wrap bg-primary-400 bg-header-mob bg-full-w bg-no-repeat px-4 pb-10 pt-6 text-surface-0 md:bg-header md:px-20 lg:px-50"
        :class="{ 'md:bg-full-h': !props.short }">
        <div v-show="showMenu && isDesktop" class="absolute inset-0 z-20 bg-overlay" />
        <div class="relative z-30 flex items-center justify-between gap-4">
            <router-link v-if="props.short" :to="{ name: ROUTES.HOME }">
                <img src="@assets/logo.svg" class="w-[100px]" />
            </router-link>
            <img v-else src="@assets/logo.svg" class="w-[100px]" />

            <div class="flex items-center gap-8">
                <a class="text-desk-h5" :href="'call:' + cleanPhone(PHONE)">
                    {{ PHONE }}
                </a>
                <div class="flex items-center gap-3">
                    <Button
                        v-if="isDesktop"
                        class="border-surface-0 bg-surface-0 text-button-primary-background"
                        label="Личный кабинет"
                        icon="pi pi-user"
                        icon-pos="right"
                        rounded
                        @click="$emit('onButtonClick', 'header_login')" />
                    <Button
                        v-if="isDesktop"
                        severity="success"
                        class=""
                        label="Оплатить задолженность"
                        icon-pos="right"
                        rounded
                        @click="showQr = true" />
                    <Button
                        class="h-min shrink-0 rounded-xl border-surface-0 bg-surface-0 text-button-primary-background"
                        :icon="showMenu ? 'pi pi-times' : 'pi pi-bars'"
                        @click="toggleMenu()" />
                </div>
            </div>
            <div
                v-show="showMenu"
                class="absolute -left-4 top-14 h-screen w-screen rounded-2xl bg-surface-50 p-6 shadow-menu md:left-auto md:right-0 md:top-12 md:h-auto md:w-72">
                <Button
                    v-if="!isDesktop"
                    class="mb-3 h-14 border-base-green bg-base-green text-button-primary-color"
                    label="Личный кабинет"
                    icon="pi pi-user"
                    icon-pos="right"
                    rounded
                    fluid
                    @click="$emit('onButtonClick', 'header_login')" />
                <Divider v-if="!isDesktop" type="dashed" />
                <div class="flex flex-col gap-6 pt-3 text-body-16 text-primary-500 md:pt-0">
                    <a href="https://hublegal.ru/business/">Для бизнеса</a>
                    <div v-if="!isDesktop" @click="showQr = true">Оплатить задолженность</div>
                </div>
            </div>
        </div>
        <div
            v-if="!props.short"
            class="mt-8 flex flex-col justify-between gap-6 lg:mt-10 lg:flex-row lg:gap-8">
            <div class="flex flex-col gap-4 md:relative md:gap-6">
                <div class="text-mob-h1 md:z-10 md:text-[44px] md:font-bold md:leading-[48px]">
                    Надоела долговая яма? Поможем!
                </div>
                <div class="md:text-body-18 text-body-14 md:z-10 md:w-96">
                    Возвращайте долг в комфортных условиях. Мы поможем: простим часть долга,
                    освободим от постоянных напоминаний, предложим решение
                </div>
                <Button
                    class="h-14 border-surface-0 bg-surface-0 text-button-primary-background md:z-10 md:w-[350px]"
                    label="Списать часть долга одним переводом"
                    :fluid="!isDesktop"
                    rounded
                    @click="$emit('onButtonClick', 'main_cta')" />
                <img
                    v-if="isDesktop"
                    class="absolute -bottom-7 left-[250px] z-0"
                    src="@assets/hammer.png" />
            </div>
            <Calc @on-button-click="$emit('onButtonClick', 'calculator_cta')" />
        </div>
    </div>
</template>
